import {
    ListCard,
    ListCardRow,
    Text,
    Breadcrumb,
    BreadcrumbItem,
    BreadcrumbLink,
    BreadcrumbList,
    BreadcrumbPage,
    BreadcrumbSeparator,
    Grid,
    OrgAvatar,
    PageHeader,
    PageHeaderBreadcrumb,
    PageHeaderIdentity,
    PageHeaderInfo,
    PageHeaderMeta,
    PageHeaderMetaDot,
    PageHeaderMetaItem,
    PageHeaderRow,
    PageHeaderTitleRow,
    StatusBadge,
    Heading,
    Button,
    Container,
    Input,
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
    Switch,
    OrganizationDetailSkeleton,
} from "@athena/ui";
import { Check, Pencil, User, X } from "lucide-react";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";
import { useMutation, useQuery } from "@apollo/client/react";
import {
    AddUserToOrganizationDocument,
    RemoveUserFromOrganizationDocument,
    UpdateUserDocument,
    UserByIdDocument,
} from "../../graphql/users.generated";
import { formatDateTime } from "../../../../global/formatHelpers";
import { useEffect, useState } from "react";
import { toast } from "sonner";
import { ErrorFallback } from "@/components/ErrorFallback";
import type { Role } from "@athena/common/graphql/generated/schema";
import { BrandsDocument } from "@/graphql/brands.generated";
import { roleLabelMap, roleVariantMap } from "@/types/Roles";
import { OrganizationSelect } from "@/components/OrganizationSelect";
import { useFeatureFlag } from "@athena/config/flags";

interface FormData {
    firstName: string;
    lastName: string;
    email: string;
    phoneNumber: string;
}

export function UserDetail() {
    const { t } = useTranslation();
    const { id } = useParams<{ id: string }>();
    const { data, loading, error, refetch } = useQuery(UserByIdDocument, {
        variables: { id: id ?? "" },
        skip: !id,
    });
    const { data: brandsData } = useQuery(BrandsDocument);
    const birthdateEnabled = useFeatureFlag("birthdateEnabled");
    const [
        updateUser,
        { loading: saving },
    ] = useMutation(UpdateUserDocument);
    const [addUserToOrganization] = useMutation(AddUserToOrganizationDocument);
    const [removeUserFromOrganization] = useMutation(RemoveUserFromOrganizationDocument);
    const user = data?.user;
    const [
        isEditing,
        setIsEditing,
    ] = useState<boolean>(false);

    const [
        formData,
        setFormData,
    ] = useState<FormData>({
        firstName: "",
        lastName: "",
        email: "",
        phoneNumber: "",
    });

    const [
        organizationId,
        setOrganizationId,
    ] = useState<string>("");

    const [
        role,
        setRole,
    ] = useState<Role>("USER");
    const [
        brand,
        setBrand,
    ] = useState<string>("RELEASE_U");
    const [
        ageRestricted,
        setAgeRestricted,
    ] = useState<boolean>(false);

    useEffect(() => {
        if (user) {
            setFormData({
                firstName: user.firstName ?? "",
                lastName: user.lastName ?? "",
                email: user.email ?? "",
                phoneNumber: "",
            });
            setOrganizationId(user.memberships[0]?.organization.id ?? "");
            setRole(user.role);
            setBrand(user.brand);
            setAgeRestricted(user.ageRestricted);
        }
    }, [user]);

    const handleChange = (name: keyof FormData, value: string) => setFormData((prev) => ({ ...prev, [name]: value }));

    const handleSave = async () => {
        if (!user) return;
        try {
            await updateUser({
                variables: {
                    id: user.id,
                    input: {
                        firstName: formData.firstName,
                        lastName: formData.lastName,
                        phoneNumber: formData.phoneNumber || undefined,
                        role,
                        brand,
                        ageRestricted,
                    },
                },
            });

            const currentOrgId = user.memberships[0]?.organization.id ?? "";
            if (organizationId !== currentOrgId) {
                if (currentOrgId) {
                    await removeUserFromOrganization({
                        variables: { input: { userId: user.id, organizationId: currentOrgId } },
                    });
                }
                if (organizationId) {
                    await addUserToOrganization({
                        variables: { input: { userId: user.id, organizationId, role: "USER" } },
                    });
                }
                await refetch();
            }

            setIsEditing(false);
            toast.success(t("success.variableUpdated", { variable: "profile" }));
        } catch {
            toast.error(t("errors.failedToSave", { variable: "profile" }));
        }
    };

    const primaryMembership = user?.memberships[0];
    const org = primaryMembership?.organization;
    const orgName = org?.parentOrganization ? org.parentOrganization.name : (org?.name ?? "—");

    if (loading) {
        return <OrganizationDetailSkeleton />;
    }

    if (error) {
        return (
            <Container maxWidth="full" className="p-6">
                <ErrorFallback error={error} />
            </Container>
        );
    }
    return (
        <>
            <PageHeader>
                <PageHeaderBreadcrumb>
                    <Breadcrumb>
                        <BreadcrumbList>
                            <BreadcrumbItem>
                                <BreadcrumbLink href="/users">{t("nav.users")}</BreadcrumbLink>
                            </BreadcrumbItem>
                            <BreadcrumbSeparator />
                            <BreadcrumbItem>
                                <BreadcrumbPage>
                                    {loading
                                        ? "…"
                                        : `${user?.firstName ?? ""} ${user?.lastName ?? ""}`.trim() || user?.email}
                                </BreadcrumbPage>
                            </BreadcrumbItem>
                        </BreadcrumbList>
                    </Breadcrumb>
                </PageHeaderBreadcrumb>

                <PageHeaderRow>
                    <PageHeaderIdentity>
                        <OrgAvatar
                            id={user?.id ?? ""}
                            fallback={`${user?.firstName?.[0] ?? ""}${user?.lastName?.[0] ?? "?"}`}
                            size="lg"
                        />
                        <PageHeaderInfo>
                            <PageHeaderTitleRow>
                                <Heading level={3}>
                                    {user?.firstName} {user?.lastName}
                                </Heading>
                            </PageHeaderTitleRow>
                            <PageHeaderMeta>
                                <PageHeaderMetaItem>{user?.email}</PageHeaderMetaItem>
                                {user?.createdAt && (
                                    <>
                                        <PageHeaderMetaDot />
                                        <PageHeaderMetaItem>
                                            {t("common.fields.joined")} {formatDateTime(user.createdAt)}
                                        </PageHeaderMetaItem>
                                    </>
                                )}
                            </PageHeaderMeta>
                        </PageHeaderInfo>
                    </PageHeaderIdentity>
                </PageHeaderRow>
            </PageHeader>

            <Grid className="p-6">
                <ListCard
                    title={t("common.fields.profileInfo")}
                    icon={<User className="text-muted-foreground h-4 w-4" />}
                    action={
                        <div className="flex flex-row gap-2">
                            {isEditing ? (
                                <>
                                    <Button variant={"outline"} onClick={() => void handleSave()} disabled={saving}>
                                        <Check className="text-muted-foreground h-4 w-4" />
                                    </Button>
                                    <Button variant={"outline"} onClick={() => setIsEditing(false)} disabled={saving}>
                                        <X className="text-muted-foreground h-4 w-4" />
                                    </Button>
                                </>
                            ) : (
                                <Button
                                    variant={"outline"}
                                    title={t("common.actions.editVariable", { variable: "Profile" })}
                                    aria-label="Edit profile"
                                    onClick={() => setIsEditing(true)}
                                >
                                    <Pencil className="text-muted-foreground h-4 w-4" />
                                </Button>
                            )}
                        </div>
                    }
                >
                    <ListCardRow>
                        <div className="grid grid-cols-1 gap-x-8 gap-y-6 py-2 sm:grid-cols-2">
                            <div className="gap-2">
                                <Text size="sm" muted>
                                    {t("common.fields.firstName")}
                                </Text>
                                {isEditing ? (
                                    <Input
                                        id="firstName"
                                        value={formData.firstName}
                                        onChange={(e) => handleChange("firstName", e.target.value)}
                                        type="text"
                                    />
                                ) : (
                                    <Text size="sm">{user?.firstName ?? "Not provided"}</Text>
                                )}
                            </div>
                            <div>
                                <Text size="sm" muted>
                                    {t("common.fields.lastName")}
                                </Text>
                                {isEditing ? (
                                    <Input
                                        id="lastName"
                                        value={formData.lastName}
                                        onChange={(e) => handleChange("lastName", e.target.value)}
                                        type="text"
                                    />
                                ) : (
                                    <Text size="sm">{user?.lastName ?? "Not provided"}</Text>
                                )}
                            </div>
                            <div>
                                <Text size="sm" muted>
                                    {t("common.fields.email")}
                                </Text>
                                <Text size="sm">{user?.email}</Text>
                            </div>
                            <div>
                                <Text size="sm" muted>
                                    {t("common.fields.phone")}
                                </Text>
                                {isEditing ? (
                                    <Input
                                        id="phoneNumber"
                                        value={formData.phoneNumber}
                                        onChange={(e) => handleChange("phoneNumber", e.target.value)}
                                        placeholder="+1 (555) 123-4567"
                                        type="text"
                                    />
                                ) : (
                                    <Text size="sm">{user?.phoneNumber ?? "Not provided"}</Text>
                                )}
                            </div>

                            <div>
                                <Text size="sm" muted>
                                    {t("common.fields.role")}
                                </Text>
                                {isEditing ? (
                                    <Select value={role} onValueChange={(v) => setRole(v as Role)}>
                                        <SelectTrigger id="role">
                                            <SelectValue />
                                        </SelectTrigger>
                                        <SelectContent>
                                            {(Object.entries(roleLabelMap) as [Role, string][]).map(
                                                ([
                                                    r,
                                                    label,
                                                ]) => (
                                                    <SelectItem key={r} value={r}>
                                                        {label}
                                                    </SelectItem>
                                                ),
                                            )}
                                        </SelectContent>
                                    </Select>
                                ) : (
                                    user && (
                                        <StatusBadge variant={roleVariantMap[user.role]} className="text-center">
                                            {roleLabelMap[user.role]}
                                        </StatusBadge>
                                    )
                                )}
                            </div>
                            <div>
                                <Text size="sm" muted>
                                    {t("common.fields.org")}
                                </Text>
                                {isEditing ? (
                                    <OrganizationSelect
                                        id="organization"
                                        value={organizationId}
                                        onValueChange={setOrganizationId}
                                        placeholder="Select an organization"
                                    />
                                ) : (
                                    <Text size="sm">{orgName}</Text>
                                )}
                            </div>
                            {/* Brand replaces a hardcoded "Age Restricted: Allowed" badge
                                that was shown for everyone — no such field exists on a
                                user, so it was decoration. Brand is real and decides
                                which courses this person can see. */}
                            <div>
                                <Text size="sm" muted>
                                    {t("common.fields.brand")}
                                </Text>
                                {isEditing ? (
                                    <Select value={brand} onValueChange={(v) => setBrand(v as string)}>
                                        <SelectTrigger id="brand">
                                            <SelectValue />
                                        </SelectTrigger>
                                        <SelectContent>
                                            {brandsData?.brands.map((brand) => (
                                                <SelectItem key={brand.id} value={brand.slug}>
                                                    {brand.name}
                                                </SelectItem>
                                            ))}
                                        </SelectContent>
                                    </Select>
                                ) : (
                                    user && (
                                        <StatusBadge variant="muted">
                                            {brandsData?.brands.find((brand) => brand.slug === user.brand)?.slug ??
                                                user.brand}
                                        </StatusBadge>
                                    )
                                )}
                            </div>
                            {/* Whether this person is barred from age-restricted content. It is the
                                gate only while birthdates are switched off — with them on, the
                                learner's date of birth decides and this is ignored (ADR 0010), which
                                is what the note below says rather than leaving a live-looking
                                control that changes nothing. */}
                            <div>
                                <Text size="sm" muted>
                                    {t("common.fields.ageRestrictContent")}
                                </Text>
                                {isEditing ? (
                                    <>
                                        {/* `h-10` matches the Input and Select beside it, so the
                                            switch sits on the same baseline as the text fields
                                            rather than riding high in a shorter row. */}
                                        <div className="flex h-10 items-center gap-2.5">
                                            {/* `aria-label` rather than a `<label htmlFor>`: Radix
                                                renders a button, and `for` only names a labelable
                                                element — it would leave the switch unnamed. */}
                                            <Switch
                                                id="ageRestricted"
                                                checked={ageRestricted}
                                                onCheckedChange={setAgeRestricted}
                                                aria-label={t("common.fields.ageRestrictContent")}
                                            />
                                            {/* The answer, beside the switch that gives it. A switch
                                                alone reads as "on", which is not an answer to a
                                                question. */}
                                            <Text size="sm">{ageRestricted ? "Yes" : "No"}</Text>
                                        </div>
                                        {birthdateEnabled && (
                                            <Text size="xs" muted>
                                                Date of birth is deciding this right now — saved, but not in effect.
                                            </Text>
                                        )}
                                    </>
                                ) : (
                                    user && (
                                        <StatusBadge variant={user.ageRestricted ? "warning" : "muted"}>
                                            {user.ageRestricted ? "Yes" : "No"}
                                        </StatusBadge>
                                    )
                                )}
                            </div>
                        </div>
                    </ListCardRow>
                </ListCard>
            </Grid>
        </>
    );
}
